import type {
  FieldRect,
  PageGeometry
} from "../../../components/Esign/geometry.ts"

// Mirrors apps.proposal.signature_slots.FIELD_TYPES in ezbuild_back.
export const FIELD_TYPES = [
  "signature",
  "initials",
  "date_signed",
  "full_name",
  "text"
] as const

export type FieldType = (typeof FIELD_TYPES)[number]

// Mirrors apps.proposal.signature_slots.PARTY_TYPES in ezbuild_back.
export const PARTY_TYPES = [
  "customer",
  "subcontractor",
  "supplier",
  "internal"
] as const

export type PartyType = (typeof PARTY_TYPES)[number]

export const RECIPIENT_ROLES = ["signer", "viewer"] as const

export type RecipientRole = (typeof RECIPIENT_ROLES)[number]

export const SUBJECT_TYPES = ["attachment", "contract", "proposal"] as const

export type SubjectType = (typeof SUBJECT_TYPES)[number]

export const PLACEMENT_MODES = ["coordinates", "anchored"] as const

export type PlacementMode = (typeof PLACEMENT_MODES)[number]

export const ENVELOPE_STATUSES = [
  "draft",
  "sent",
  "partially_signed",
  "signed",
  "completed",
  "declined",
  "expired",
  "voided"
] as const

export type EnvelopeStatus = (typeof ENVELOPE_STATUSES)[number]

export const RECIPIENT_STATUSES = [
  "pending",
  "sent",
  "delivered",
  "viewed",
  "consented",
  "signed",
  "declined",
  "expired"
] as const

export type RecipientStatus = (typeof RECIPIENT_STATUSES)[number]

export const OTP_CHANNELS = ["email", "sms"] as const

export type OtpChannel = (typeof OTP_CHANNELS)[number]

export const FIELD_ORIGINS = ["template", "ad_hoc"] as const

export type FieldOrigin = (typeof FIELD_ORIGINS)[number]

export const READINESS_CODES = [
  "not_a_draft",
  "unsupported_channel",
  "missing_contact",
  "missing_address",
  "record_archived",
  "subject_unavailable",
  "newer_version",
  "document_replaced",
  "document_archived",
  "document_trashed",
  "document_deleted",
  "document_changed",
  "signer_without_signature",
  "field_off_page"
] as const

export type ReadinessCode = (typeof READINESS_CODES)[number]

export interface EnvelopeRecipient {
  id: string
  party_type: PartyType
  role: RecipientRole
  name: string
  email: string
  mobile: string
  signing_order: number
  status: RecipientStatus
  user: string | null
  template_slot: string
  field_count?: number
  signed?: string | null
  /** The colleague who countersigned for the company; null for everyone else. */
  signed_by?: string | null
}

export interface EnvelopeSubject {
  type: SubjectType
  id: string
  name: string
  version: number | string | null
}

export interface EnvelopeDocument {
  page_count: number
  pages: PageGeometry[]
  has_form_fields: boolean
  url: string
}

/** The completed document's fingerprint and the timestamp authority's time on it. */
export interface EnvelopeSeal {
  sha256: string
  timestamp: string
  timestamp_authority: string
  has_validation_data: boolean
  certificate_sha256: string
  certificate_url: string
}

export interface SigningEnvelope {
  id: string
  lead: string
  status: EnvelopeStatus
  otp_channel: OtpChannel
  subject: EnvelopeSubject | null
  recipients: EnvelopeRecipient[]
  placement_mode?: PlacementMode
  document?: EnvelopeDocument | null
  created: string
  created_by: string | null
  /** When an unsigned request stops being signable; null until sent. */
  expires_at?: string | null
  /** The signing order being waited on; null unless the request is sent or partly signed. */
  turn?: number | null
  /** Ids of the signers at the turn who have not signed. */
  waiting_on?: string[]
  declined?: { recipient: string; reason: string } | null
  /** Null until the request is completed; absent from a server older than the field. */
  seal?: EnvelopeSeal | null
  /** Whether a notice of cancellation goes out beside the document; absent from an older server. */
  requires_notice?: boolean
  /** Gives the buyers five business days to cancel instead of three. */
  buyer_65_or_older?: boolean
  /** Null until the request is sent with one. */
  notice?: EnvelopeNotice | null
}

export interface EnvelopeNotice {
  name: string
  page_count: number
  pages: PageGeometry[]
}

/** One recipient as the Recipients step sends it; `id` names a stored row. */
export interface RecipientInput {
  id?: string
  party_type: PartyType
  role: RecipientRole
  name: string
  email: string
  mobile: string
  signing_order: number
  /** A template signer slot to take; `""` gives back the one the row held. */
  template_slot?: string
}

export interface EnvelopeSlot {
  key: string
  name: string
  party_type: PartyType
  recipient_id: string | null
}

export interface EnvelopeCreateBody {
  /** Null raises a standalone request on a Company Files document. */
  lead: string | null
  subject_type: SubjectType
  subject_id: string
  recipients: RecipientInput[]
}

export interface EnvelopeUpdateBody {
  recipients?: RecipientInput[]
  buyer_65_or_older?: boolean
}

interface FieldDescription {
  recipient: string
  field_type: FieldType
  label: string
  required: boolean
}

type Unplaced<T> = { [Key in keyof T]: T[Key] | null }

export interface SignatureField extends FieldDescription, Unplaced<FieldRect> {
  id: string
  block_id?: string | null
  origin?: FieldOrigin
  template_slot?: string
}

export interface PlacedField extends FieldDescription, FieldRect {
  id: string
  origin?: FieldOrigin
}

export interface FieldCreateBody extends FieldDescription, FieldRect {
  id: string
}

export const PATCHABLE_FIELD_KEYS = [
  "page",
  "x",
  "y",
  "width",
  "height",
  "recipient",
  "required",
  "label"
] as const satisfies readonly (keyof PlacedField)[]

export type FieldPatchBody = Partial<
  Pick<PlacedField, (typeof PATCHABLE_FIELD_KEYS)[number]>
>

export interface ReadinessProblem {
  code: ReadinessCode
  message: string
  field_ids: string[]
  recipient_ids: string[]
}

export interface Readiness {
  ready: boolean
  problems: ReadinessProblem[]
}
